import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { flagPost } from '@/lib/api/posts';
import type { FlagReason } from '@/lib/api/posts';
import { useAuth } from '@/lib/hooks/useAuth';

const schema = z.object({
  reason: z.enum(['spam', 'harassment', 'misinformation', 'other'], {
    required_error: 'Please select a reason',
  }),
  note: z.string().max(500, 'Note must be 500 characters or fewer').optional(),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ReportPostDialog({ postId, open, onOpenChange }: Props) {
  const { profile } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const { mutate, isPending } = useMutation({
    mutationFn: (values: FormValues) => {
      if (!profile) throw new Error('Not authenticated');
      return flagPost(postId, profile.id, values.reason as FlagReason, values.note);
    },
    onSuccess: () => {
      onOpenChange(false);
      reset();
      toast.success('Report submitted. Our team will review it shortly.');
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      if (code === '23505') {
        toast.error("You've already reported this post.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    },
  });

  function onSubmit(values: FormValues) {
    mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report post</DialogTitle>
          <DialogDescription>
            Help us keep CPN safe. Reports are confidential and will not be shared with the author.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason <span aria-hidden="true">*</span></Label>
            <select
              id="reason"
              {...register('reason')}
              className="w-full rounded-md border border-muted/30 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
              aria-describedby={errors.reason ? 'reason-error' : undefined}
            >
              <option value="">Select a reason…</option>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment</option>
              <option value="misinformation">Misinformation</option>
              <option value="other">Other</option>
            </select>
            {errors.reason && (
              <p id="reason-error" className="text-xs text-red-500" aria-live="polite">
                {errors.reason.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="note">
              Additional context{' '}
              <span className="font-normal text-muted">(optional)</span>
            </Label>
            <Textarea
              id="note"
              {...register('note')}
              placeholder="Briefly describe the issue…"
              className="min-h-[80px] resize-none border-muted/30 text-sm"
              maxLength={500}
              aria-describedby={errors.note ? 'note-error' : undefined}
            />
            {errors.note && (
              <p id="note-error" className="text-xs text-red-500" aria-live="polite">
                {errors.note.message}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-navy text-cream hover:bg-navy-deep"
              disabled={isPending}
            >
              {isPending ? 'Submitting…' : 'Submit report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
